import React, { useState } from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Bar, BarChart, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

const tick = { fill: 'var(--dv-muted)', fontSize: 12 };
const tooltipStyle = { background: 'var(--dv-panel)', color: 'var(--dv-ink)', border: '1px solid var(--dv-line)', borderRadius: 8, maxWidth: 260, whiteSpace: 'normal' };

export function GoalsTrend({ data }) {
  const { t } = useTranslation('dashboardV2');
  const [mode, setMode] = useState('matchday');
  const cumulative = mode === 'cumulative';
  let goalsFor = 0;
  let goalsAgainst = 0;
  const points = data.map((point) => {
    goalsFor += point.goalsFor;
    goalsAgainst += point.goalsAgainst;
    const round = point.round?.match(/^(?:J(?:ornada)?\s*)?(\d+)\b/i)?.[1];
    return { ...point, goalsFor: cumulative ? goalsFor : point.goalsFor,
      goalsAgainst: cumulative ? goalsAgainst : point.goalsAgainst,
      label: round ? t('matchdayShort', { round }) : point.date };
  });
  const Chart = cumulative ? LineChart : BarChart;
  return <>
    <ToggleButtonGroup exclusive size="small" value={mode} aria-label={t('goalsView')}
      onChange={(_event, value) => { if (value) setMode(value); }} sx={{ mb: 2 }}>
      <ToggleButton value="matchday">{t('goalsPerMatchday')}</ToggleButton>
      <ToggleButton value="cumulative">{t('cumulativeGoals')}</ToggleButton>
    </ToggleButtonGroup>
    <div className="dv-chart-legend">
      <span className="dv-goals-for">{t('goalsForLabel')}</span>
      <span className="dv-goals-against">{t('goalsAgainstLabel')}</span>
    </div>
    <div className="dv-chart"><div className="dv-chart-canvas" style={{ minWidth: points.length * 65 }}>
      <ResponsiveContainer width="100%" height={210}>
        <Chart data={points} margin={{ left: -24, right: 12, top: 15, bottom: 0 }} accessibilityLayer>
          <XAxis dataKey="label" interval={0} axisLine={false} tickLine={false} tick={tick} />
          <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={tick} />
          <Tooltip contentStyle={tooltipStyle} labelFormatter={(label, payload) => {
            const point = payload?.[0]?.payload;
            return cumulative ? t('goalsThroughRound', { round: label })
              : point ? `${label} · ${point.opponent} · ${t(point.isHome ? 'homeVenue' : 'awayVenue')}` : label;
          }} />
          {cumulative ? <>
            <Line type="linear" dataKey="goalsFor" name={t('goalsForLabel')} stroke="var(--dv-radar-own)" strokeWidth={3} isAnimationActive={false} />
            <Line type="linear" dataKey="goalsAgainst" name={t('goalsAgainstLabel')} stroke="var(--dv-radar-rival)" strokeWidth={3} strokeDasharray="5 3" isAnimationActive={false} />
          </> : <>
            <Bar dataKey="goalsFor" name={t('goalsForLabel')} fill="var(--dv-radar-own)" radius={[4, 4, 0, 0]} isAnimationActive={false} />
            <Bar dataKey="goalsAgainst" name={t('goalsAgainstLabel')} fill="var(--dv-radar-rival)" radius={[4, 4, 0, 0]} isAnimationActive={false} />
          </>}
        </Chart>
      </ResponsiveContainer>
    </div></div>
    <p className="dv-muted dv-caption">{t('goalsTrendCaption')}</p>
    <div className="dv-chart-data"><table aria-label={t('goalsTrendTitle')}>
      <caption>{t(cumulative ? 'cumulativeGoals' : 'goalsPerMatchday')}</caption>
      <thead><tr><th>{t('round')}</th><th>{t('opponent')}</th><th>{t('goalsForLabel')}</th><th>{t('goalsAgainstLabel')}</th></tr></thead>
      <tbody>{points.map((point) => <tr key={point.matchId}>
        <th scope="row">{point.label}</th><td>{point.opponent} · {t(point.isHome ? 'homeVenue' : 'awayVenue')}</td>
        <td>{point.goalsFor}</td><td>{point.goalsAgainst}</td>
      </tr>)}</tbody>
    </table></div>
  </>;
}

export function ClassificationTrend({ data, compact = false, teamName, comparison }) {
  const { t } = useTranslation('dashboardV2');
  const comparisonPositions = new Map(comparison?.data.rounds.map((point) => [point.round, point.position]));
  const points = data.rounds.map((point) => ({
    ...point, comparisonPosition: comparisonPositions.get(point.round) ?? null,
    label: t('matchdayShort', { round: point.round }),
  }));
  const primaryName = teamName || t('position');
  return <>
    {teamName && <div className="dv-chart-legend cs-position-legend">
      <span className="cs-position-primary">{primaryName}</span>
      {comparison && <span className="cs-position-comparison">{comparison.name}</span>}
    </div>}
    <div className="dv-chart"><div className="dv-chart-canvas" style={{ minWidth: points.length * 55 }}>
      <ResponsiveContainer width="100%" height={compact ? 160 : 210}>
        <LineChart data={points} margin={{ left: -18, right: 20, top: 15, bottom: 0 }} accessibilityLayer>
          <XAxis dataKey="label" interval={0} axisLine={false} tickLine={false} tick={tick} />
          <YAxis reversed domain={[1, Math.max(2, data.groupSize, comparison?.data.groupSize || 0)]} allowDecimals={false} axisLine={false} tickLine={false} tick={tick} />
          <Tooltip position={compact ? { x: 0, y: 0 } : undefined} wrapperStyle={{ maxWidth: '100%' }}
            contentStyle={{ ...tooltipStyle, maxWidth: '100%', boxSizing: 'border-box', overflowWrap: 'anywhere' }}
            formatter={(value, name) => [value, name]} />
          <Line type="linear" dataKey="position" name={primaryName} stroke="var(--dv-green)" strokeWidth={3} dot={{ r: 4 }} connectNulls={false} isAnimationActive={false} />
          {comparison && <Line type="linear" dataKey="comparisonPosition" name={comparison.name}
            stroke="var(--dv-radar-rival)" strokeDasharray="5 3" strokeWidth={3} dot={{ r: 3 }}
            connectNulls={false} isAnimationActive={false} />}
        </LineChart>
      </ResponsiveContainer>
    </div></div>
    <p className="dv-muted dv-caption">{t('classificationTrendCaption')}</p>
    <div className="dv-chart-data"><table aria-label={t('classificationTrendTitle')}>
      <thead><tr><th>{t('round')}</th><th>{primaryName}</th>{comparison && <th>{comparison.name}</th>}</tr></thead>
      <tbody>{points.map((point) => <tr key={point.round}><th scope="row">{point.label}</th>
        <td>{point.position ?? t('unavailable')}</td>
        {comparison && <td>{point.comparisonPosition ?? t('unavailable')}</td>}
      </tr>)}</tbody>
    </table></div>
  </>;
}
